import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ChevronDown,
  Loader2,
  Receipt,
  RefreshCw,
  Search,
  ShieldAlert,
  X,
} from "lucide-react";
import OperatorConsoleShell from "../components/operator/OperatorConsoleShell";
import { useMediaQuery } from "../hooks/useMediaQuery";
import TicketAnalyticsStrip from "../components/operator/TicketAnalyticsStrip";
import TicketInspectPanel from "../components/operator/TicketInspectPanel";
import { selectAppUser } from "../redux/appAuthSlice";
import {
  clearHouseTickets,
  fetchHouseTicketDetail,
  fetchHouseTickets,
  fetchOperatorDashboard,
} from "../redux/operatorConsoleSlice";
import { formatCash } from "../utils/operatorConsoleHelpers";
import { formatRelative } from "../utils/bettorsDeskHelpers";
import {
  formatTicketCount,
  highLiabilityIds,
  TICKET_SORTS,
  TICKET_STATUS_FILTERS,
  ticketListSummary,
  ticketPlacedDateKey,
} from "../utils/ticketsDeskHelpers";
import { buildTimelineSections } from "../utils/activityHelpers";
import {
  ROSTER_MOBILE_INITIAL,
  ROSTER_MOBILE_STEP,
  ROSTER_STACK_BREAKPOINT,
  sliceRosterSections,
} from "../utils/rosterMobileHelpers";
import "../bettors-desk.css";

const TicketsDesk = () => {
  const { houseId } = useParams();
  const dispatch = useDispatch();
  const userId = useSelector(selectAppUser)?._id;

  const {
    houses,
    dashboard,
    dashboardHouseId,
    dashboardStatus,
    houseTickets,
    houseTicketsPagination,
    houseTicketsStatus,
    houseTicketsError,
    loadingMoreHouseTickets,
    houseTicketsFilteredPulse,
    houseTicketsHousePulse,
  } = useSelector((s) => s.operatorConsole);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [sort, setSort] = useState("recent");
  const [selectedId, setSelectedId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [rosterVisibleCount, setRosterVisibleCount] = useState(ROSTER_MOBILE_INITIAL);

  const [detailCache, setDetailCache] = useState({});
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const detailCacheRef = useRef(detailCache);
  const detailRef = useRef(null);
  const isStackedLayout = useMediaQuery(ROSTER_STACK_BREAKPOINT);

  useEffect(() => {
    detailCacheRef.current = detailCache;
  }, [detailCache]);

  const bet = useMemo(
    () => houses.find((h) => String(h._id) === String(houseId)),
    [houses, houseId]
  );

  const hasActiveFilters = Boolean(debouncedSearch || (status && status !== "all"));

  useEffect(() => {
    if (houseId) dispatch(fetchOperatorDashboard(houseId));
  }, [dispatch, houseId]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const listParams = useMemo(
    () => ({
      houseId,
      operatorUserId: userId,
      page: 1,
      limit: 20,
      search: debouncedSearch,
      status,
      sort,
      append: false,
    }),
    [houseId, userId, debouncedSearch, status, sort]
  );

  const reload = useCallback(() => {
    if (!houseId || !userId) return;
    dispatch(fetchHouseTickets(listParams));
  }, [dispatch, houseId, userId, listParams]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    return () => dispatch(clearHouseTickets());
  }, [dispatch]);

  const loadDetail = useCallback(
    async (ticketId, { force = false } = {}) => {
      if (!houseId || !userId || !ticketId) return;
      const key = String(ticketId);
      const cached = detailCacheRef.current[key];
      if (cached?.fetched && !force) return;

      setDetailLoading(true);
      setDetailError(null);
      try {
        const result = await dispatch(
          fetchHouseTicketDetail({ houseId, ticketId: key, operatorUserId: userId })
        ).unwrap();
        setDetailCache((prev) => ({
          ...prev,
          [key]: { fetched: true, ticket: result.ticket },
        }));
      } catch (err) {
        setDetailError(String(err));
        setDetailCache((prev) => ({
          ...prev,
          [key]: { fetched: true, ticket: null },
        }));
      } finally {
        setDetailLoading(false);
      }
    },
    [dispatch, houseId, userId]
  );

  useEffect(() => {
    if (houseTicketsStatus !== "succeeded" || houseTickets.length === 0) {
      if (houseTicketsStatus === "succeeded" && houseTickets.length === 0) {
        setSelectedId(null);
      }
      return;
    }

    setSelectedId((prev) => {
      if (prev && houseTickets.some((row) => String(row.ticketId) === prev)) return prev;
      return String(houseTickets[0].ticketId);
    });
  }, [houseTickets, houseTicketsStatus]);

  useEffect(() => {
    if (!selectedId) return;
    loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  const handleSelect = (row) => {
    const id = String(row.ticketId);
    setSelectedId(id);
    setDetailError(null);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setDetailCache({});
    try {
      await Promise.all([
        dispatch(fetchHouseTickets(listParams)).unwrap(),
        dispatch(fetchOperatorDashboard(houseId)).unwrap(),
      ]);
      if (selectedId) {
        await loadDetail(selectedId, { force: true });
      }
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoadMore = () => {
    if (!houseId || !userId || loadingMoreHouseTickets || !houseTicketsPagination?.hasNextPage) {
      return;
    }
    dispatch(
      fetchHouseTickets({
        houseId,
        operatorUserId: userId,
        page: (houseTicketsPagination.page || 1) + 1,
        limit: houseTicketsPagination.limit || 20,
        search: debouncedSearch,
        status,
        sort,
        append: true,
      })
    );
  };

  const sections = useMemo(
    () => buildTimelineSections(houseTickets, ticketPlacedDateKey),
    [houseTickets]
  );

  useEffect(() => {
    setRosterVisibleCount(ROSTER_MOBILE_INITIAL);
  }, [debouncedSearch, status, sort]);

  const { displaySections, rosterHiddenCount } = useMemo(() => {
    if (!isStackedLayout) {
      return { displaySections: sections, rosterHiddenCount: 0 };
    }
    return sliceRosterSections(
      sections,
      rosterVisibleCount,
      selectedId,
      (row) => row.ticketId
    );
  }, [sections, isStackedLayout, rosterVisibleCount, selectedId]);

  useEffect(() => {
    if (!selectedId || !isStackedLayout) return;
    const timer = window.setTimeout(() => {
      detailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => window.clearTimeout(timer);
  }, [selectedId, isStackedLayout]);

  const selectedRow = useMemo(
    () => houseTickets.find((r) => String(r.ticketId) === String(selectedId)),
    [houseTickets, selectedId]
  );

  const selectedTicket = useMemo(() => {
    if (!selectedId) return null;
    const cached = detailCache[selectedId]?.ticket;
    if (cached) return cached;
    return selectedRow || null;
  }, [selectedId, detailCache, selectedRow]);

  const dashboardReady =
    dashboardStatus === "succeeded" && String(dashboardHouseId) === String(houseId);

  const analyticsLoading =
    houseTicketsStatus === "loading" && !houseTickets.length && !houseTicketsFilteredPulse;

  const riskTicketIds = useMemo(
    () =>
      dashboardReady
        ? highLiabilityIds(dashboard?.openTicketsHighlights?.topByLiability)
        : new Set(),
    [dashboardReady, dashboard]
  );

  const pulse = useMemo(() => {
    const filtered = houseTicketsFilteredPulse;
    const house = houseTicketsHousePulse;
    const matched = houseTicketsPagination?.totalCount ?? houseTickets.length;

    if (hasActiveFilters && filtered) {
      return {
        tickets: filtered.ticketCount ?? matched,
        stake: filtered.sumStake ?? 0,
        exposure: filtered.pendingLiability ?? 0,
        open: filtered.openTicketCount ?? 0,
        bettors: filtered.distinctBettors ?? null,
      };
    }

    if (house) {
      return {
        tickets: house.ticketCount ?? dashboard?.activity?.totalTickets ?? matched,
        stake: house.sumStake ?? dashboard?.activity?.totalStakesReceived ?? 0,
        exposure: house.pendingLiability ?? 0,
        open: house.openTicketCount ?? dashboard?.activity?.openTickets ?? 0,
        bettors: house.distinctBettors ?? dashboard?.customers?.totalUnique ?? null,
      };
    }

    return {
      tickets: dashboardReady ? dashboard?.activity?.totalTickets ?? matched : matched,
      stake: dashboardReady ? dashboard?.activity?.totalStakesReceived ?? 0 : 0,
      exposure: 0,
      open: dashboardReady ? dashboard?.activity?.openTickets ?? 0 : 0,
      bettors: dashboardReady ? dashboard?.customers?.totalUnique ?? null : null,
    };
  }, [
    hasActiveFilters,
    houseTicketsFilteredPulse,
    houseTicketsHousePulse,
    houseTicketsPagination?.totalCount,
    houseTickets.length,
    dashboardReady,
    dashboard,
  ]);

  const headerSubtitle = useMemo(() => {
    const houseName = bet?.name || "House";
    if (houseTicketsStatus === "loading" && !houseTickets.length) {
      return houseName;
    }
    const count = hasActiveFilters
      ? houseTicketsPagination?.totalCount ?? houseTickets.length
      : pulse.tickets;
    return `${houseName} · ${formatTicketCount(count)}`;
  }, [
    bet?.name,
    houseTicketsStatus,
    houseTickets.length,
    hasActiveFilters,
    houseTicketsPagination?.totalCount,
    pulse.tickets,
  ]);

  const bettorsPath = `/app/bethouse/manage/${houseId}/bettors`;

  if (!houseId) {
    return (
      <OperatorConsoleShell activeSection="tickets" mainClassName="oc-desk-main">
        <div className="bd-page bd-tickets-desk">
          <div className="bd-loading">Missing house.</div>
        </div>
      </OperatorConsoleShell>
    );
  }

  return (
    <OperatorConsoleShell activeSection="tickets" mainClassName="oc-desk-main">
      <div className="bd-page bd-tickets-desk">
      <header className="bd-header bd-tickets-header">
        <div className="bd-header-copy">
          <p className="bd-eyebrow">
            <Receipt size={9} />
            Ticket desk
          </p>
          <span className="bd-header-title">
            {headerSubtitle}
            {houseTicketsStatus === "succeeded" && pulse.tickets > 0
              ? ` · ${pulse.open} open · ${formatCash(pulse.stake, { compact: true })} staked · ${formatCash(pulse.exposure, { compact: true })} exposure`
              : ""}
          </span>
        </div>
        <button
          type="button"
          className="bd-icon-btn"
          onClick={handleRefresh}
          disabled={refreshing || houseTicketsStatus === "loading"}
          aria-label="Refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
        </button>
      </header>

      <div className="bd-desk-body">
      <div className="bd-bettors-command">
        <aside className="bd-roster-rail">
          <div className="bd-roster-toolbar">
            <div className="bd-search bd-search-compact">
              <Search size={13} />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by bettor…"
                aria-label="Search tickets"
              />
              {search ? (
                <button
                  type="button"
                  className="bd-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear"
                >
                  <X size={13} />
                </button>
              ) : null}
            </div>

            <div className="bd-chip-row bd-chip-row-compact" role="group" aria-label="Status">
              {TICKET_STATUS_FILTERS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`bd-chip ${status === value ? "bd-chip-active" : ""}`}
                  onClick={() => setStatus(value)}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="bd-chip-row bd-chip-row-compact" role="group" aria-label="Sort">
              {TICKET_SORTS.map(({ value, label }) => (
                <button
                  key={value}
                  type="button"
                  className={`bd-chip ${sort === value ? "bd-chip-active" : ""}`}
                  onClick={() => setSort(value)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <section className="bd-roster bd-roster-rail-scroll">
            <header className="bd-rail-head">
              <h2>Lane</h2>
              <span className="tabular-nums">
                {hasActiveFilters
                  ? houseTicketsPagination?.totalCount ?? houseTickets.length
                  : pulse.tickets}
              </span>
            </header>

            {houseTicketsStatus === "loading" && !houseTickets.length && (
              <div className="bd-loading bd-loading-compact">
                <Loader2 size={18} className="animate-spin" />
                <span>Loading…</span>
              </div>
            )}

            {houseTicketsError && !houseTickets.length && (
              <div className="bd-loading bd-loading-compact">
                <p>{String(houseTicketsError)}</p>
                <button type="button" onClick={reload}>
                  Retry
                </button>
              </div>
            )}

            {!houseTickets.length && houseTicketsStatus === "succeeded" && (
              <p className="bd-empty bd-empty-compact">No tickets match.</p>
            )}

            {displaySections.map((section) => (
              <div key={section.dateKey} className="bd-day">
                <h3 className="bd-day-head">
                  <span>{section.title}</span>
                  <span className="bd-day-line" aria-hidden />
                </h3>
                <ul className="bd-roster-list">
                  {section.data.map((row) => {
                    const id = String(row.ticketId);
                    const active = selectedId === id;
                    const { meta, legsLabel } = ticketListSummary(row);
                    const isOpen =
                      String(row.status).toLowerCase() === "pending" ||
                      Number(row.openLegs) > 0;
                    const highRisk = isOpen && riskTicketIds.has(id);
                    return (
                      <li key={id}>
                        <button
                          type="button"
                          className={`bd-roster-row bd-ticket-row bd-ticket-row-${meta.tone} ${
                            highRisk ? "bd-ticket-row-risk" : ""
                          } ${active ? "bd-roster-row-active" : ""}`}
                          onClick={() => handleSelect(row)}
                        >
                          <span className={`bd-ticket-status-dot bd-ticket-status-${meta.tone}`} aria-hidden />
                          <div className="bd-roster-copy">
                            <strong className="tabular-nums">
                              {formatCash(row.stake, { compact: true })}
                            </strong>
                            <span>
                              {row.username || "Bettor"}
                              {legsLabel ? ` · ${legsLabel}` : ""}
                              {highRisk ? (
                                <em className="bd-risk-badge">
                                  <ShieldAlert size={8} aria-hidden />
                                  High liability
                                </em>
                              ) : null}
                            </span>
                          </div>
                          <div className="bd-roster-side">
                            <em className={`bd-tag bd-tag-${meta.tone}`}>{meta.label}</em>
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}

            {rosterHiddenCount > 0 ? (
              <button
                type="button"
                className="bd-roster-load-more"
                onClick={() =>
                  setRosterVisibleCount((count) => count + ROSTER_MOBILE_STEP)
                }
              >
                <ChevronDown size={13} aria-hidden />
                Show more
                <span className="tabular-nums">{rosterHiddenCount}</span>
              </button>
            ) : null}

            {houseTicketsPagination?.hasNextPage ? (
              <button
                type="button"
                className="bd-load-more bd-load-more-compact"
                onClick={handleLoadMore}
                disabled={loadingMoreHouseTickets}
              >
                {loadingMoreHouseTickets ? (
                  <>
                    <Loader2 size={13} className="animate-spin" />
                    Loading…
                  </>
                ) : (
                  "Load more"
                )}
              </button>
            ) : null}
          </section>
        </aside>

        <div className="bd-bettors-workspace">
          <TicketAnalyticsStrip
            layout="workspace"
            pulseRaw={houseTicketsFilteredPulse}
            loading={analyticsLoading}
            filtered={hasActiveFilters}
            housePulseRaw={houseTicketsHousePulse}
            highlights={dashboardReady ? dashboard?.openTicketsHighlights : null}
            rows={houseTickets}
            hasMorePages={Boolean(houseTicketsPagination?.hasNextPage)}
          />

          <div ref={detailRef} className="bd-detail-wrap">
            <TicketInspectPanel
              ticket={selectedTicket}
              ticketLoading={detailLoading && !!selectedId && !detailCache[selectedId]?.fetched}
              ticketError={detailError}
              bettorsPath={bettorsPath}
            />
          </div>
        </div>
      </div>
      </div>
      </div>
    </OperatorConsoleShell>
  );
};

export default TicketsDesk;
